import { BusyIndicator } from "client/Components";
import { DestinationWindow, EmailDestinationApp } from "client/Api/Apps/common";
import { EmailListItemModel } from "client/Api/Email/email";
import {
	useStableImperativeCallback,
	useUserTimezone,
	useValueEqualMemo
} from "client/Hooks";
import {
	isNil,
	isNotNil
} from "client/Misc/utils";
import {
	getEffectiveOwaEmailClient,
	OwaEmailClient,
	WorkspaceEmailClient
} from "client_legacy/Email/emailClient";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { Guid } from "client_legacy/ndaw/guid";
import {
	EmailLiveInformationProvider,
	InterestedEmailParty
} from "client_legacy/Workspace/LiveInformation/Email/provider";
import {
	ContentKind,
	convertFolderIdentifierToModel,
	convertMailboxIdentifierToModel,
	FolderIdentifier,
	FolderIdentifierKind,
	Interests,
	ItemsResult,
	ItemsResultStatus,
	loadMore,
	LoadMoreRequestModel,
	MailboxIdentifier,
	MailboxIdentifierKind,
	UnreadCountResult,
	WellKnownFolder
} from "client/Api/Workspace/LiveInformation/Email/emailLiveInformation";
import { DimensionToRender } from "../../RenderState/common";
import {
	ReactElement,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { LiveTileContent } from "../LiveTileContent";
import { TileContext } from "../TileContext";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { TileTabProps } from "../TileTabSelector";
import { EmailListItem } from "./EmailListItem";
import { TimeZone } from "client/DateAndTime/timezones";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { EmailAppTab } from "client/Api/Workspace/workspace";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type EmailTileNavigationOptions = { folderId?: string; mailbox?: string | null; };

export type EmailLiveTileContentProps = {
	destinationApp: EmailDestinationApp;
	destinationWindow: DestinationWindow;
	tabs: EmailAppTab[];
	provider: EmailLiveInformationProvider;
	onNavigate: (options?: EmailTileNavigationOptions) => void;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const EmailLiveTileContent = (props: EmailLiveTileContentProps): ReactElement => {
	const {
		destinationApp,
		destinationWindow,
		tabs: tabsFromProps,
		provider,
		onNavigate,
		onOpenTileMenu
	} = props;

	const [activeTabId, setActiveTabId] = useState<Guid | undefined>(
		(): Guid | undefined => tabsFromProps[0]?.uniqueId
	);

	const {
		id,
		icon,
		name,
		dimension,
		environmentAppId,
		interactionState,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const [tabs, setTabs] = useState<readonly EmailTab[]>(
		() => tabsFromProps.map((x): EmailTab => ({
			uniqueId: x.uniqueId,
			name: x.name,
			mailbox: x.mailbox,
			folder: x.folder,
			contentKind: x.contentKind,
		}))
	);

	const userTimezone: TimeZone = useUserTimezone();

	const activeTab = useMemo(
		(): EmailTab | undefined => {
			return tabs.find(x => x.uniqueId === activeTabId);
		},
		[activeTabId, tabs]
	);

	const navigateHandler = useCallback(
		(tab: EmailTab | undefined): void => {
			addAnalyticsEvent();

			if (isNil(tab)) {
				onNavigate();
				return;
			}

			let mailbox: string | null;

			switch (tab.mailbox.kind) {
				case MailboxIdentifierKind.Default:
					mailbox = null;
					break;
				case MailboxIdentifierKind.Specific:
					mailbox = tab.mailbox.smtpAddress;
					break;
				default:
					never(tab.mailbox);
			}

			switch (tab.folder.kind) {
				case FolderIdentifierKind.Specific:
					onNavigate({ folderId: tab.folder.id, mailbox });
					break;
				case FolderIdentifierKind.WellKnown:
					switch (tab.folder.folder) {
						case WellKnownFolder.Inbox:
							onNavigate({ mailbox });
							break;
						default:
							never(tab.folder.folder);
					}
					break;
				default:
					never(tab.folder);
			}
		},
		[addAnalyticsEvent, onNavigate]
	);

	const effectiveEmailClient = useMemo(
		(): WorkspaceEmailClient | OwaEmailClient => {
			switch (destinationApp) {
				case EmailDestinationApp.Email:
					return new WorkspaceEmailClient(destinationWindow === DestinationWindow.NewWindow);
				case EmailDestinationApp.OutlookWebApp:
					return getEffectiveOwaEmailClient(destinationWindow === DestinationWindow.NewWindow);
				default:
					never(destinationApp);
			}
		},
		[destinationApp, destinationWindow]
	);

	const createNewHandler = useCallback(
		(): void => {
			addAnalyticsEvent();

			if (!effectiveEmailClient.supportsEmailCompose) {
				console.error("The email client does not support email composing", effectiveEmailClient);
				return;
			}
			effectiveEmailClient.composeEmail();
		},
		[addAnalyticsEvent, effectiveEmailClient]
	);

	const defaultActions = useMemo(
		(): readonly Action<ActionContext>[] => listDefaultActions({
			new: createNewHandler
		}),
		[createNewHandler]
	);

	const actionContext = useMemo(
		(): ActionContext => ({
			isExchangeEnabled: provider.isExchangeEnabled,
			effectiveEmailClient
		}),
		[effectiveEmailClient, provider.isExchangeEnabled]
	);

	const unreadCountChangeHandler = useCallback(
		(tab: EmailTab, count: number): void => {
			setTabs(
				(x) => x.map(
					(x): EmailTab => {
						if (x.uniqueId === tab.uniqueId) {
							return {
								...x,
								count,
								suffix: undefined
							};
						}
						return x;
					}
				)
			);
		},
		[]
	);

	return (
		<LiveTileContent
			name={name}
			icon={icon}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			<LiveTileContent.Header
				icon={icon}
				actions={defaultActions}
				actionContext={actionContext}
			>
				<LiveTileContent.TabSelector
					value={activeTabId}
					tabs={tabs}
					onChange={setActiveTabId}
					tileId={id}
					tileName={name}
				/>
			</LiveTileContent.Header>

			<LiveTileContent.Content>
				{tabs.map((x): ReactElement => (
					<Tab
						key={x.uniqueId}
						isActive={x.uniqueId === activeTabId}
						data={x}
						dimension={dimension}
						emailClient={effectiveEmailClient}
						onUnreadCountChanged={(count) => unreadCountChangeHandler(x, count)}
						provider={provider}
						timezone={userTimezone}
						addAnalyticsEvent={addAnalyticsEvent}
					/>
				))}

			</LiveTileContent.Content>

			{provider.isExchangeEnabled && (
				<LiveTileContent.Footer
					name={name}
					onClick={() => navigateHandler(activeTab)}
				/>
			)}
		</LiveTileContent>
	);
};
EmailLiveTileContent.displayName = "EmailLiveTileContent";

type ActionContext = {
	isExchangeEnabled: boolean;
	effectiveEmailClient: WorkspaceEmailClient | OwaEmailClient;
};

type ActionHandlers = {
	new: () => void;
};

function listDefaultActions(
	handlers: ActionHandlers
): readonly Action<ActionContext>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "new-email",
			title: localize("_NewEmailFull"),
			icon: "plus",
			canExecute: (x) => x.isExchangeEnabled
				&& x.effectiveEmailClient.supportsEmailCompose,
			execute: () => handlers.new()
		}
	];
}

type TabProps = {
	isActive: boolean;
	data: EmailTab;
	dimension: DimensionToRender;
	emailClient: WorkspaceEmailClient | OwaEmailClient;
	onUnreadCountChanged: (count: number) => void;
	provider: EmailLiveInformationProvider;
	timezone: TimeZone;
	addAnalyticsEvent(): void;
};

const Tab = (props: TabProps): ReactElement | null => {
	const {
		isActive,
		data,
		dimension,
		emailClient,
		onUnreadCountChanged,
		provider,
		timezone,
		addAnalyticsEvent
	} = props;

	const [requestedPage, setRequestedPage] = useState<number>(0);

	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});
	const stableOnUnreadCountChanged: typeof onUnreadCountChanged= useStableImperativeCallback(onUnreadCountChanged);

	const shouldUseWideTemplate: boolean = dimension.kind === "normal"
		&& dimension.dimension
			.columnSpan > 4;

	const smtpAddress: string | null = useMemo(
		(): string | null => {
			switch (data.mailbox.kind) {
				case MailboxIdentifierKind.Default:
					return null;
				case MailboxIdentifierKind.Specific:
					return data.mailbox.smtpAddress;
				default:
					never(data.mailbox);
			}
		},
		[data.mailbox]
	);

	const party = useMemo(
		(): InterestedEmailParty => {
			return {
				partyId: data.uniqueId,
				resourceId: {
					mailbox: data.mailbox,
					folder: data.folder
				},
				updateUnreadCount: (result: UnreadCountResult | nil) => {
					if (isNotNil(result) && result.failed === false) {
						stableOnUnreadCountChanged(result.amount);
					}
				},
				updateItems: (result: ItemsResult | null) => {
					if (!result) {
						setLoadingState({
							isLoading: false,
							response: {
								status: ItemsResultStatus.GeneralFailure
							}
						});
						return;
					}

					setLoadingState({
						isLoading: false,
						response: result
					});
				},
				informAboutExchangeNotBeingConfigured: () => {
					setLoadingState({
						isLoading: false,
						hasGlobalFailure: true
					});
				},
				informAboutGlobalRetrievalFailure: () => {
					setLoadingState({
						isLoading: false,
						hasGlobalFailure: true
					});
				},
				informAboutConsecutiveErrors: () => {
					setLoadingState({
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					});
				}
			};
		},
		[
			data.folder,
			data.mailbox,
			data.uniqueId,
			stableOnUnreadCountChanged
		]
	);

	const interests = useValueEqualMemo(
		(): Interests => {
			const result: Interests = { unreadCount: true };
			if (isActive && requestedPage < 1) {
				switch (data.contentKind) {
					case ContentKind.Unread:
						result.unreadItems = true;
						break;
					case ContentKind.ReadAndUnread:
						result.items = true;
						break;
					default:
						never(data.contentKind);
				}
			}

			return result;
		},
		[
			data.contentKind,
			isActive,
			requestedPage
		]
	);

	useEffect(
		(): (() => void) => {
			provider.register(party, interests);

			return (): void => {
				provider.unregister(party);
			};
		},
		[
			interests,
			party,
			provider
		]
	);

	useEffect(
		(): void => {
			if (!isActive) {
				setRequestedPage(0);
			}
		},
		[isActive]
	);

	const loadMoreCancellationRef: RefObject<AbortController | null> =
		useRef<AbortController>(null);

	const loadMoreHandler = useCallback(
		async (): Promise<void> => {
			if (loadingState.isLoading
				|| loadingState.hasGlobalFailure
				|| loadingState.hasTooManyConsecutiveErrors
				|| loadingState.response.status !== ItemsResultStatus.Success
				|| !loadingState.response.items.hasMore
			) {
				return;
			}

			loadMoreCancellationRef.current
				?.abort();

			const cancellation = new AbortController();
			loadMoreCancellationRef.current = cancellation;

			try {
				const pageToLoad: number = requestedPage + 1;

				setLoadingState({
					isLoading: true,
					items: loadingState.response
						.items
				});

				setRequestedPage(pageToLoad);

				const model: LoadMoreRequestModel = {
					mailbox: convertMailboxIdentifierToModel(party.resourceId.mailbox),
					folder: convertFolderIdentifierToModel(party.resourceId.folder),
					pageNumber: pageToLoad,
					shouldRestrictToUnreadItems: data.contentKind === ContentKind.Unread,
				};

				const page: LazyLoadPageResult<EmailListItemModel> = await loadMore(model, cancellation.signal);

				setLoadingState({
					isLoading: false,
					response: {
						status: ItemsResultStatus.Success,
						items: {
							hasMore: page.hasMore,
							items: [
								...loadingState.response
									.items
									.items,
								...page.items
							]
						}
					}
				});
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				setLoadingState({
					isLoading: false,
					response: {
						status: ItemsResultStatus.Success,
						items: {
							...loadingState.response
								.items,
							hasMore: false,
						}
					}
				});
			}
		},
		[
			data.contentKind,
			loadingState,
			party,
			requestedPage
		]
	);

	useEffect(
		(): (() => void) => {
			return (): void => {
				loadMoreCancellationRef.current
					?.abort();
			};
		},
		[]
	);

	const scrollHandler = useCallback(
		(scrollTop: number): void => {
			if (requestedPage < 1) {
				return;
			}

			if (scrollTop <= 0) {
				loadMoreCancellationRef.current
					?.abort();
				setRequestedPage(0);
			}
		},
		[requestedPage]
	);

	if (!isActive) {
		return null;
	}

	const renderPage = (page: LazyLoadPageResult<EmailListItemModel>): ReactElement => {
		const { items, hasMore } = page;

		if (items.length === 0) {
			let key: LocalizationKey;
			switch (data.contentKind) {
				case ContentKind.Unread:
					key = "_NoUnreadMessages";
					break;
				case ContentKind.ReadAndUnread:
					key = "_YouDontHaveAnyEmailMessagesInThisFolder";
					break;
				default:
					never(data.contentKind);
			}

			return (
				<LiveTileContent.Message
					text={localize(key)}
				/>
			);
		}

		return (
			<>
				<LiveTileContent.List trackingId="email-items-list">
					{items.map((x): ReactElement => (
						<EmailListItem
							key={x.emailId.id}
							message={x}
							smtpAddress={smtpAddress}
							shouldUseWideTemplate={shouldUseWideTemplate}
							emailClient={emailClient}
							timezone={timezone}
							onClick={addAnalyticsEvent}
						/>
					))}
				</LiveTileContent.List>

				{hasMore && (
					<LiveTileContent.LoadMoreButton
						onClick={loadMoreHandler}
					/>
				)}
			</>
		);
	};

	const renderContent = (): ReactElement => {
		if (loadingState.isLoading) {
			return (
				<>
					{loadingState.items && (
						<>
							{renderPage(loadingState.items)}
						</>
					)}
				</>
			);
		}

		if (loadingState.hasGlobalFailure) {
			if (!provider.isExchangeAvailable) {
				return (
					<LiveTileContent.Message
						text={localize("_TheConnectionWithExchangeIsNotConfiguredThereforeWeCouldNotRetrieveYourUnreadEmailMessages")}
					/>
				);
			} else {
				return (
					<LiveTileContent.Message
						text={localize("_ErrorDuringUnreadMessagesRetrieval")}
					/>
				);
			}
		}

		if (loadingState.hasTooManyConsecutiveErrors) {
			return (
				<TileRefresh onRefresh={() => provider.forceReload()} />
			);
		}

		const { response } = loadingState;

		switch (response.status) {
			case ItemsResultStatus.GeneralFailure:
				return (
					<LiveTileContent.Message
						text={localize("_ErrorDuringUnreadMessagesRetrieval")}
					/>
				);
			case ItemsResultStatus.NoAccess:
				return (
					<LiveTileContent.Message
						text={localize("_CannotAccessTheFolderPleaseContactYourAdminForMoreInformation")}
					/>
				);
			case ItemsResultStatus.Success:
				return renderPage(response.items);
			default:
				never(response);
		}
	};

	return (
		<LiveTileContent.ContentStack>
			<LiveTileContent.BusyIndicatorContainer>
				{loadingState.isLoading && (
					<BusyIndicator />
				)}

				<LiveTileContent.Scrollable onScroll={scrollHandler}>
					{renderContent()}
				</LiveTileContent.Scrollable>
			</LiveTileContent.BusyIndicatorContainer>
		</LiveTileContent.ContentStack>
	);
};
Tab.displayName = "Tab";

type EmailTab = TileTabProps & {
	contentKind: ContentKind;
	mailbox: MailboxIdentifier;
	folder: FolderIdentifier;
};

type LoadingState = {
	isLoading: true;
	items?: LazyLoadPageResult<EmailListItemModel>;
} | {
	isLoading: false;
} & (
		{
			hasTooManyConsecutiveErrors: true;
			hasGlobalFailure?: undefined;
		}
		|
		{
			hasGlobalFailure: true;
			hasTooManyConsecutiveErrors?: undefined;
		}
		|
		{
			hasTooManyConsecutiveErrors?: undefined;
			hasGlobalFailure?: undefined;
			response: ItemsResult;
		}
	);
